// Month-grid overview of team leaves. Rows = employees w/ overlapping
// leave; columns = days of month. Sticky first column so long teams stay
// scannable while horizontally scrolling.
export default function TeamCalendar({ month, onMonthChange, data }) {
  const [y, m] = month.split("-").map((s) => parseInt(s, 10));
  const daysInMonth = new Date(y, m, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const items = data?.items || [];

  // Group by employee for row-per-person view
  const byEmp = new Map();
  for (const l of items) {
    const key = l.employee_id || "?";
    if (!byEmp.has(key)) {
      byEmp.set(key, {
        name: l.employee_name || key,
        employee_no: l.employee_no,
        department: l.employee_department,
        leaves: [],
      });
    }
    byEmp.get(key).leaves.push(l);
  }
  const rows = Array.from(byEmp.values());

  const dayIsInLeave = (leaves, dayNum) => {
    const iso = `${y}-${String(m).padStart(2, "0")}-${String(dayNum).padStart(2, "0")}`;
    return leaves.find((l) => iso >= l.start_date && iso <= l.end_date);
  };

  const shift = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    onMonthChange(d.toISOString().slice(0, 7));
  };

  const monthLabel = new Date(y, m - 1, 1).toLocaleDateString(undefined, {
    month: "long", year: "numeric",
  });

  return (
    <div className="p-5" data-testid="team-calendar">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => shift(-1)} className="text-xs font-mono border border-zinc-950 px-2 py-1 hover:bg-zinc-950 hover:text-white" data-testid="calendar-prev">← PREV</button>
          <div className="text-lg font-semibold" data-testid="calendar-month-label">{monthLabel}</div>
          <button onClick={() => shift(1)} className="text-xs font-mono border border-zinc-950 px-2 py-1 hover:bg-zinc-950 hover:text-white" data-testid="calendar-next">NEXT →</button>
        </div>
        <div className="text-xs font-mono text-zinc-500">{rows.length} PEOPLE · {items.length} LEAVES</div>
      </div>

      {rows.length === 0 ? (
        <div className="border border-zinc-200 py-10 text-center text-xs font-mono text-zinc-400" data-testid="team-calendar-empty">
          NO LEAVES OVERLAP THIS MONTH
        </div>
      ) : (
        <div className="overflow-x-auto border border-zinc-200">
          <table className="min-w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-950">
              <tr>
                <th className="text-left px-2 py-2 sticky left-0 bg-zinc-50 min-w-[180px] border-r border-zinc-200 z-10">Employee</th>
                {days.map((d) => (
                  <th key={d} className="w-6 text-center font-mono text-[9px] px-0.5 py-2 text-zinc-500">{d}</th>
                ))}
              </tr>
            </thead>
            <tbody data-testid="team-calendar-tbody">
              {rows.map((row) => (
                <tr key={row.name} className="border-b border-zinc-100">
                  <td className="px-2 py-1.5 sticky left-0 bg-white border-r border-zinc-200 z-10">
                    <div className="font-medium">{row.name}</div>
                    {row.department && <div className="text-[9px] font-mono text-zinc-400">{row.department}</div>}
                  </td>
                  {days.map((d) => {
                    const hit = dayIsInLeave(row.leaves, d);
                    if (!hit) return <td key={d} className="w-6 h-8 border-r border-zinc-100" />;
                    const bg = hit.status === "approved"
                      ? "bg-emerald-500"
                      : hit.status === "pending"
                      ? "bg-amber-400"
                      : "bg-zinc-300";
                    return (
                      <td
                        key={d}
                        title={`${hit.type} · ${hit.start_date}→${hit.end_date} (${hit.status})`}
                        className={`w-6 h-8 border-r border-zinc-100 ${bg}`}
                        data-testid={`cal-${row.name}-${d}`}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex items-center gap-4 p-3 text-[10px] font-mono text-zinc-500 border-t border-zinc-200">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 inline-block" /> APPROVED</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-400 inline-block" /> PENDING</span>
          </div>
        </div>
      )}
    </div>
  );
}
